import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CrcPipe } from '../../../../shared/pipes/crc.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { CountdownComponent } from '../../../../shared/ui/countdown/countdown.component';
import { PpCoinComponent } from '../../../../shared/ui/pp-coin/pp-coin.component';
import { CHECKOUT_COPY, PAYMENT_DETAILS } from '../../checkout-copy';
import { scrollBehavior } from '../../../../shared/utils/scroll';

type PaymentDataMethod = 'sinpe' | 'transfer';

/**
 * Override puntual de copy para reutilizar el modal fuera del checkout de
 * carrito (contexto PRECOMPRA). Solo admite las dos líneas cuyo copy de
 * apartado es falso en precompra: `layawayHint` y `ctaPrimary`. Todo lo demás
 * sigue saliendo de `CHECKOUT_COPY.paymentData` (D14).
 */
export interface PaymentDataCopyOverrides {
  readonly layawayHint?: string;
  readonly ctaPrimary?: string;
  /**
   * Línea informativa de Perrón Points (contexto precompra). El PADRE la pasa
   * solo con sesión iniciada (la precompra de invitado no acumula); sin ella no
   * se pinta, de modo que el checkout de carrito normal no se ve afectado.
   */
  readonly perronPointsHint?: string;
}

/** Tipos de imagen admitidos por el backend (regla `mimes`). */
const ALLOWED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
]);
/** Tamaño máximo del comprobante (espejo de la regla `max:5120` de Laravel). */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * Paso final para SINPE y transferencia: muestra los datos de pago + el importe y
 * exige el comprobante para finalizar. El pedido NO está creado todavía:
 * «Finalizar» lo crea con la imagen ya adjunta (create-on-finalize) y solo
 * entonces se navega a éxito. Sin imagen no hay pedido.
 *
 * El comprobante es OBLIGATORIO: el CTA primario está deshabilitado hasta
 * adjuntarlo. El secundario es «Cancelar» (Escape también): cierra el modal sin
 * crear nada, el carrito queda intacto. Reusa el patrón de focus-trap, guardas
 * SSR y scroll-lock.
 */
@Component({
  selector: 'app-payment-data-modal',
  imports: [ButtonComponent, CountdownComponent, CrcPipe, PpCoinComponent],
  templateUrl: './payment-data-modal.component.html',
  styleUrl: './payment-data-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentDataModalComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = input<boolean>(false);
  readonly method = input<PaymentDataMethod>('sinpe');
  /** Modalidad apartado: muestra abono inicial + saldo restante en vez del total. */
  readonly isLayaway = input<boolean>(false);
  /** Importe a pagar AHORA (total del pedido, o abono del 30% si es apartado). */
  readonly amountColones = input<number>(0);
  /** Saldo restante tras el abono inicial (solo relevante en apartado). */
  readonly remainingColones = input<number>(0);
  /** Mientras se crea el preorder: deshabilita los CTA y muestra «loading». */
  readonly submitting = input<boolean>(false);
  /** Error devuelto por el backend al finalizar (p. ej. stock agotado). */
  readonly errorMessage = input<string | null>(null);
  /**
   * Overrides de copy (contexto precompra). `null` (default) = copy del
   * checkout de carrito, que NO cambia.
   */
  readonly copyOverrides = input<PaymentDataCopyOverrides | null>(null);
  /**
   * Reserva flotante (contexto precompra, Bloque 2): expiración ABSOLUTA de la
   * sesión para mostrar el countdown también con este modal abierto. `null`
   * (default) = sin countdown (el checkout de carrito NO cambia). El evento de
   * expiración lo gestiona el countdown del PADRE (una única fuente); este es
   * solo de display.
   */
  readonly countdownExpiresAt = input<string | null>(null);
  /** Plantilla con `{mm:ss}` para el countdown (copy centralizado del padre). */
  readonly countdownLabel = input<string>('{mm:ss}');

  /** Emite el comprobante adjunto al confirmar (obligatorio: nunca null). */
  readonly finalize = output<File>();
  /** Emite cuando el cliente cancela (botón «Cancelar» / Escape). Nada creado. */
  readonly dismiss = output<void>();

  protected readonly copy = CHECKOUT_COPY.paymentData;
  protected readonly details = PAYMENT_DETAILS;

  /** Comprobante seleccionado (obligatorio para finalizar). */
  protected readonly selectedFile = signal<File | null>(null);

  /** Error de validación del propio modal (tipo/tamaño de imagen) antes de enviar. */
  protected readonly localError = signal<string | null>(null);

  /** Error mostrado: prioriza el local (validación previa) sobre el del backend. */
  protected readonly displayError = computed(() => this.localError() ?? this.errorMessage());

  protected readonly heading = computed(() =>
    this.method() === 'transfer' ? this.copy.transfer.title : this.copy.sinpe.title,
  );

  /** Línea de condición bajo el importe (override de precompra o copy de apartado). */
  protected readonly layawayHint = computed(
    () => this.copyOverrides()?.layawayHint ?? this.copy.layawayHint,
  );

  /** Texto del CTA primario (override de precompra o copy del checkout). */
  protected readonly ctaPrimary = computed(
    () => this.copyOverrides()?.ctaPrimary ?? this.copy.ctaPrimary,
  );

  /** Línea de Perrón Points (solo cuando el padre la pasa: precompra con sesión). */
  protected readonly perronPointsHint = computed(
    () => this.copyOverrides()?.perronPointsHint ?? null,
  );

  /** El CTA primario solo se habilita con comprobante y sin envío en curso. */
  protected readonly canFinalize = computed(
    () => this.selectedFile() !== null && !this.submitting(),
  );

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private readonly errorRef = viewChild<ElementRef<HTMLElement>>('errorEl');
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor() {
    // Al aparecer un error (validación local de imagen o rechazo del backend) lo
    // traemos a la vista dentro del propio scroll del modal. El <p> se pinta con
    // @if, así que diferimos con queueMicrotask para leer la ref ya renderizada
    // (mismo patrón de queueMicrotask que el foco de apertura de abajo).
    effect(() => {
      const hasError = this.displayError() !== null;
      if (!hasError || !isPlatformBrowser(this.platformId)) return;
      queueMicrotask(() =>
        this.errorRef()?.nativeElement.scrollIntoView({
          behavior: scrollBehavior(),
          block: 'center',
        }),
      );
    });

    // Reset del archivo + guardar/restaurar foco + bloqueo de scroll, según `open`.
    effect(() => {
      const isOpen = this.open();
      if (!isPlatformBrowser(this.platformId)) return;
      document.body.style.overflow = isOpen ? 'hidden' : '';

      if (isOpen) {
        this.selectedFile.set(null);
        this.localError.set(null);
        this.previouslyFocusedElement =
          (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => {
          const root = this.dialogRef()?.nativeElement;
          if (root) this.getFocusableElements(root)[0]?.focus();
        });
      } else if (this.previouslyFocusedElement) {
        const el = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;
        queueMicrotask(() => {
          if (document.contains(el)) el.focus({ preventScroll: true });
        });
      }
    });

    // Focus trap: Escape CANCELA (emite dismiss); Tab/Shift+Tab ciclan dentro.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.open()) return;

          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            if (!this.submitting()) this.dismiss.emit();
            return;
          }

          if (e.key !== 'Tab') return;

          const modal = this.dialogRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) {
            e.preventDefault();
            return;
          }

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.selectedFile.set(null);
      return;
    }

    const error = this.validateImage(file);
    if (error) {
      this.localError.set(error);
      this.selectedFile.set(null);
      // Permite reintentar el mismo archivo tras corregir (vuelve a disparar `change`).
      input.value = '';
      return;
    }

    this.localError.set(null);
    this.selectedFile.set(file);
  }

  /** Valida tipo y tamaño del comprobante (espejo de la validación del backend). */
  private validateImage(file: File): string | null {
    const type = file.type.toLowerCase();
    const isImage = type ? ALLOWED_IMAGE_TYPES.has(type) : /\.(jpe?g|png|webp|avif)$/i.test(file.name);
    if (!isImage) {
      return 'El comprobante debe ser una imagen JPG, PNG, WebP o AVIF.';
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return 'La imagen supera el máximo de 5 MB. Sube una más ligera.';
    }
    return null;
  }

  protected onFinalize(): void {
    const file = this.selectedFile();
    if (!file || this.submitting()) return;
    this.finalize.emit(file);
  }

  protected onDismiss(): void {
    if (this.submitting()) return;
    this.dismiss.emit();
  }

  /** Devuelve los elementos enfocables dentro del modal en orden DOM. */
  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
